import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ArrowDownLeft, Save, RefreshCw } from "lucide-react";

interface ReceiveAddr {
  id: string;
  network: string;
  address: string;
  is_active: boolean;
}

const AdminReceiveAddresses = () => {
  const { toast } = useToast();
  const [addresses, setAddresses] = useState<ReceiveAddr[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => { fetchAddresses(); }, []);

  const fetchAddresses = async () => {
    setLoading(true);
    const { data } = await supabase.from("receive_addresses").select("*").order("network");
    if (data) setAddresses(data as ReceiveAddr[]);
    setLoading(false);
  };

  const handleSave = async (addr: ReceiveAddr) => {
    setSaving(addr.id);
    try {
      const { error } = await supabase.from("receive_addresses").update({ address: addr.address }).eq("id", addr.id);
      if (error) throw error;
      toast({ title: "تم الحفظ", description: `تم تحديث عنوان ${addr.network}` });
    } catch (e: any) {
      toast({ title: "خطأ", description: e.message, variant: "destructive" });
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return <Card className="glass"><CardContent className="p-6"><div className="animate-pulse text-muted-foreground">جاري التحميل...</div></CardContent></Card>;
  }

  return (
    <Card className="glass">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-foreground">
            <ArrowDownLeft className="w-5 h-5 text-primary" />
            عناوين الاستقبال
          </CardTitle>
          <Button variant="outline" size="sm" onClick={fetchAddresses}><RefreshCw className="w-4 h-4" /></Button>
        </div>
        <p className="text-sm text-muted-foreground">أدخل عنوان المحفظة لكل شبكة ليظهر للمستخدمين عند الاستقبال</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {addresses.map(addr => (
          <div key={addr.id} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
            <span className="text-sm font-medium text-foreground w-40 shrink-0">{addr.network}</span>
            <Input
              value={addr.address}
              onChange={e => setAddresses(prev => prev.map(a => a.id === addr.id ? { ...a, address: e.target.value } : a))}
              placeholder="أدخل العنوان..."
              dir="ltr"
              className="bg-input border-border font-mono text-xs flex-1"
            />
            <Button size="sm" onClick={() => handleSave(addr)} disabled={saving === addr.id}>
              <Save className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default AdminReceiveAddresses;
